'use no memo'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, Navigate, Outlet, createFileRoute, useNavigate, useRouterState } from '@tanstack/react-router'
import { toast } from 'sonner'
import { Info, PlusIcon } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { SpaceAllDocsView } from '../components/SpaceAllDocsView'
import { SpaceSectionMenu } from '../components/SpaceSectionMenu'
import type { ProjectDocument } from '@/lib/db/rxdb/schemas/project'
import { useLayoutRouteState } from '@/routes/features/projects/hooks/useProjectChildRouteType'
import {
  useChildProjects,
  useProjectMutations,
  useRootProjects,
} from '@/lib/db/rxdb/hooks'
import useLoginStore from '@/store/LoginStore'
import { Loading } from '@/components/app/indicators/Loading'
import { usePageLayout } from '@/hooks/usePageLayout'
import TaskSidebar from '@/components/app/sidebars/task'
import useProjectFilterStore from '@/store/ProjectFilterStore'
import useProjectViewPreferencesStore from '@/store/ProjectViewPreferencesStore'
import { EMPTY_FILTERS } from '@/lib/types/filter'
import { StarToggle } from '@/components/app/StarToggle'
import { Button } from '@/components/ui/button'
import { UnifiedIconPicker } from '@/components/app/pickers/unified-icon-picker'
import { ProjectIcon } from '@/lib/utils/projectIcon'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

type SpaceTab = 'tasks' | 'views' | 'docs' | 'projects'
const SPACE_TABS: Array<SpaceTab> = ['tasks', 'views', 'docs', 'projects']

const SPACE_VIEW_ROUTES = {
  list: '/$workspaceSlug/spaces/$spaceKey/list',
  board: '/$workspaceSlug/spaces/$spaceKey/board',
  calendar: '/$workspaceSlug/spaces/$spaceKey/calendar',
  gantt: '/$workspaceSlug/spaces/$spaceKey/gantt',
} as const

export const Route = createFileRoute(
  '/_AppWrapper/_AppLayout/$workspaceSlug/spaces/$spaceKey',
)({
  component: SpaceDetailsPage,
  validateSearch: (search: Record<string, unknown>): { tab?: SpaceTab } => {
    const tab = search.tab as string | undefined
    if (tab && SPACE_TABS.includes(tab as SpaceTab)) return { tab: tab as SpaceTab }
    return {}
  },
})

const projectFormSchema = z.object({
  name: z
    .string()
    .min(1, 'Project name is required')
    .max(100, 'Project name must be 100 characters or less'),
  projectKey: z
    .string()
    .min(1, 'Project key is required')
    .max(10, 'Project key must be 10 characters or less')
    .regex(/^[A-Z0-9]+$/, 'Project key must contain only uppercase letters and numbers'),
  icon: z.string().nullable(),
  iconType: z.string().nullable(),
  iconColor: z.string().nullable(),
})

type ProjectFormData = z.infer<typeof projectFormSchema>

export default function SpaceDetailsPage() {
  const { workspaceSlug, spaceKey } = Route.useParams()
  const navigate = useNavigate()

  const basePath = `/${workspaceSlug}/spaces/${spaceKey}`
  const { isActive, childRouteType } = useLayoutRouteState(basePath)

  const workspaceId = useLoginStore((state) => state.workspaceId)
  // Read `tab` straight from router location instead of Route.useSearch() —
  // during child→parent navigation transitions the parent route's validated
  // search briefly reports undefined even though the URL already carries
  // `?tab=...`, which caused the tab click to bounce back to /list.
  const searchTab = useRouterState({
    select: (state) => {
      const raw = (state.location.search as { tab?: string } | undefined)?.tab
      if (raw && SPACE_TABS.includes(raw as SpaceTab)) return raw as SpaceTab
      return undefined
    },
  })

  const [isAddProjectOpen, setIsAddProjectOpen] = useState(false)

  const { projects: rootProjects, isLoading: spacesLoading } = useRootProjects()
  const { createProject } = useProjectMutations()

  const space = useMemo<ProjectDocument | null>(() => {
    return (
      rootProjects.find((entry) => {
        if (entry.isDeleted) return false
        if (entry.projectKey.toLowerCase() !== spaceKey.toLowerCase()) return false
        if (workspaceId && entry.envId !== workspaceId) return false
        return true
      }) ?? null
    )
  }, [rootProjects, spaceKey, workspaceId])

  const { projects: childProjects } = useChildProjects(space?.id)

  const activeChildProjects = useMemo(() => {
    return (childProjects ?? []).filter((p) => !p.isDeleted)
  }, [childProjects])

  const childProjectIds = useMemo(
    () => activeChildProjects.map((p) => p.id),
    [activeChildProjects],
  )

  // Ensure a pinned project filter always exists for this space
  const upsertFilters = useProjectFilterStore((s) => s.upsertFilters)
  const existingFilters = useProjectFilterStore(
    (s) => s.filters[space?.id ?? ''] ?? EMPTY_FILTERS,
  )

  useEffect(() => {
    if (!space || childProjectIds.length === 0) return
    // Pinned: ungrouped, so it applies to every OR group.
    const hasProjectFilter = existingFilters.some(
      (f) => f.fieldKey === 'project' && f.group === undefined,
    )
    if (!hasProjectFilter) {
      upsertFilters(space.id, [
        { fieldKey: 'project', operator: 'includes', values: childProjectIds },
      ])
    }
  }, [space?.id, childProjectIds, existingFilters, upsertFilters])

  const preferredView = useProjectViewPreferencesStore(
    (s) => (space ? s.getPreferences(space.id).viewType : 'list'),
  )

  // Determine the active tab based on routing state
  const activeTab: SpaceTab =
    childRouteType === 'view'
      ? 'tasks'
      : childRouteType === 'views'
        ? 'views'
        : (searchTab ?? 'tasks')

  const handleTabChange = useCallback(
    (tab: SpaceTab) => {
      if (tab === 'tasks') {
        // Navigate to the preferred view route
        navigate({
          to: SPACE_VIEW_ROUTES[preferredView],
          params: { workspaceSlug, spaceKey },
        })
      } else if (tab === 'views') {
        navigate({
          to: '/$workspaceSlug/spaces/$spaceKey/views',
          params: { workspaceSlug, spaceKey },
        })
      } else {
        navigate({
          to: '/$workspaceSlug/spaces/$spaceKey',
          params: { workspaceSlug, spaceKey },
          search: { tab },
          replace: true,
        })
      }
    },
    [navigate, workspaceSlug, spaceKey, preferredView],
  )

  // Breadcrumb
  const breadcrumbContent = useMemo(() => {
    if (!space) return null
    return (
      <>
        <Link
          to="/$workspaceSlug/spaces/$spaceKey"
          params={{ workspaceSlug, spaceKey }}
          className="flex items-center gap-1 hover:text-foreground transition-colors truncate"
        >
          <ProjectIcon iconType={space.iconType} icon={space.icon} iconColor={space.iconColor} size={14} />
          <span className="truncate">{space.name || 'Untitled space'}</span>
        </Link>
        <span aria-hidden className="text-muted-foreground/50">/</span>
        {/* The tab row is gone (design 26c): the section is the title's dropdown. */}
        <SpaceSectionMenu
          spaceId={space.id}
          workspaceSlug={workspaceSlug}
          spaceKey={spaceKey}
          activeSection={activeTab}
          onSectionChange={handleTabChange}
        />
        <StarToggle entityType="project" entityId={space.id} size={14} />
      </>
    )
  }, [space, workspaceSlug, spaceKey, activeTab, handleTabChange])

  // When on a view route or the base space page, show breadcrumb.
  // When on "other" child routes (projects/pages), let the child own breadcrumb.
  usePageLayout({
    sidebarContent: <TaskSidebar />,
    // Skip breadcrumb while `space` hasn't resolved — otherwise we'd call
    // setBreadcrumb(null) and leave the bar blank if the effect doesn't
    // re-fire once space loads.
    ...(childRouteType === 'other' || !breadcrumbContent
      ? {}
      : { breadcrumb: breadcrumbContent }),
  }, [space?.id, space?.name, space?.icon, space?.iconType, space?.iconColor, workspaceSlug, spaceKey, activeTab, childRouteType])

  const form = useForm<ProjectFormData>({
    resolver: zodResolver(projectFormSchema),
    defaultValues: { name: '', projectKey: '', icon: null, iconType: null, iconColor: null },
  })

  const handleCreateProject = async (data: ProjectFormData) => {
    if (!workspaceId || !space) {
      toast.error('Missing context to create a project')
      return
    }
    try {
      const projectId = crypto.randomUUID()
      await createProject({
        id: projectId,
        projectKey: data.projectKey,
        name: data.name,
        icon: data.icon,
        iconType: data.iconType,
        iconColor: data.iconColor,
        parentProjectId: space.id,
      })
      form.reset()
      setIsAddProjectOpen(false)
      toast.success('Project created')
      navigate({
        to: '/$workspaceSlug/spaces/$spaceKey/projects/$projectKey',
        params: { workspaceSlug, spaceKey, projectKey: data.projectKey.toLowerCase() },
      })
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message || 'Unable to create project' : 'Unable to create project',
      )
    }
  }

  // During route transitions the router updates location before unmounting
  // stale child layouts — bail out so our Navigate doesn't race the parent's.
  if (!isActive) return null

  // For "other" child routes (projects, pages), just render the child
  if (childRouteType === 'other') return <Outlet />

  // When on the bare base URL (no tab selected), redirect to preferred Tasks view.
  // IMPORTANT: only redirect when searchTab is truly absent — if the user picked
  // Docs/Projects the searchTab is set and we must NOT redirect back to /list.
  if (childRouteType === 'none' && searchTab === undefined) {
    return (
      <Navigate
        to={SPACE_VIEW_ROUTES[preferredView]}
        params={{ workspaceSlug, spaceKey }}
        replace
      />
    )
  }

  if (spacesLoading) {
    return (
      <div className="flex min-h-full flex-1 items-center justify-center">
        <Loading />
      </div>
    )
  }

  if (!space) {
    return (
      <div className="flex min-h-full flex-1 items-center justify-center px-4">
        <div className="text-center text-muted-foreground">
          Space not found or may have been removed.
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      {/* Tab Content */}
      <div className="flex-1 min-h-0 overflow-hidden">
        {childRouteType === 'view' || childRouteType === 'views' ? (
          /* Tasks view or Views tab — child route owns content */
          <Outlet />
        ) : activeTab === 'projects' ? (
          <div className="h-full overflow-y-auto p-4 md:p-8">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                  Projects
                </h2>
                <Dialog
                  open={isAddProjectOpen}
                  onOpenChange={(open) => {
                    setIsAddProjectOpen(open)
                    if (!open) form.reset()
                  }}
                >
                  <DialogTrigger asChild>
                    <Button type="button" variant="outline" size="sm">
                      <PlusIcon className="size-3.5 mr-1" />
                      New project
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Create project</DialogTitle>
                      <DialogDescription>
                        Organise tasks for a specific goal.
                      </DialogDescription>
                    </DialogHeader>
                    <form
                      className="grid gap-4"
                      onSubmit={form.handleSubmit(handleCreateProject)}
                    >
                      <div className="grid gap-2">
                        <Label htmlFor="project-name">Project name</Label>
                        <div className="flex items-center gap-2">
                          <UnifiedIconPicker
                            iconType={form.watch('iconType')}
                            icon={form.watch('icon')}
                            iconColor={form.watch('iconColor')}
                            onValueChange={(value) => {
                              if (value) {
                                form.setValue('icon', value.icon)
                                form.setValue('iconType', value.iconType)
                                form.setValue('iconColor', value.iconColor ?? null)
                              } else {
                                form.setValue('icon', null)
                                form.setValue('iconType', null)
                                form.setValue('iconColor', null)
                              }
                            }}
                          >
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              className="flex h-[36px] w-[36px] items-center justify-center rounded-sm border border-input bg-background text-muted-foreground"
                              aria-label={form.watch('icon') ? 'Change icon' : 'Select icon'}
                              disabled={form.formState.isSubmitting}
                            >
                              <ProjectIcon
                                iconType={form.watch('iconType')}
                                icon={form.watch('icon')}
                                iconColor={form.watch('iconColor')}
                                size={20}
                              />
                            </Button>
                          </UnifiedIconPicker>
                          <div className="flex-1">
                            <Input
                              id="project-name"
                              {...form.register('name')}
                              placeholder="e.g. Website Redesign"
                              autoFocus
                              disabled={form.formState.isSubmitting}
                              className="rounded-sm"
                            />
                            {form.formState.errors.name && (
                              <p className="text-sm text-destructive mt-1">
                                {form.formState.errors.name.message}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="grid gap-2">
                        <div className="flex items-center gap-1.5">
                          <Label htmlFor="project-key">Project Key</Label>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Info className="size-3.5 text-muted-foreground cursor-help" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="max-w-xs text-sm">
                                  All tasks in this project will be prefixed with this key
                                  (e.g., MOLY-123). Alphanumeric only, max 10 characters.
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                        <Input
                          id="project-key"
                          {...form.register('projectKey')}
                          onChange={(e) => {
                            const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10)
                            form.setValue('projectKey', value)
                          }}
                          placeholder="e.g. WEB"
                          maxLength={10}
                          disabled={form.formState.isSubmitting}
                          className="w-1/2 rounded-sm font-mono"
                        />
                        {form.formState.errors.projectKey && (
                          <p className="text-sm text-destructive mt-1">
                            {form.formState.errors.projectKey.message}
                          </p>
                        )}
                      </div>
                      <DialogFooter>
                        <DialogClose asChild>
                          <Button type="button" variant="outline" disabled={form.formState.isSubmitting}>
                            Cancel
                          </Button>
                        </DialogClose>
                        <Button type="submit" disabled={form.formState.isSubmitting}>
                          {form.formState.isSubmitting ? 'Creating...' : 'Create'}
                        </Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>

              {activeChildProjects.length === 0 ? (
                <p className="text-sm text-muted-foreground">No projects yet.</p>
              ) : (
                <div className="grid gap-2">
                  {activeChildProjects.map((project) => (
                    <button
                      key={project.id}
                      type="button"
                      onClick={() => {
                        navigate({
                          to: '/$workspaceSlug/spaces/$spaceKey/projects/$projectKey',
                          params: {
                            workspaceSlug,
                            spaceKey,
                            projectKey: project.projectKey.toLowerCase(),
                          },
                        })
                      }}
                      className="flex items-center gap-3 rounded-lg border border-border bg-card px-4 py-3 text-left transition-colors hover:bg-accent/50"
                    >
                      <ProjectIcon iconType={project.iconType} icon={project.icon} iconColor={project.iconColor} size={18} />
                      <span className="font-medium text-sm">{project.name}</span>
                      <span className="ml-auto text-xs text-muted-foreground font-mono">
                        {project.projectKey}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          // The Docs tab is "All docs" (design 22a): the space's own docs and
          // every project's, grouped by where each one lives.
          <SpaceAllDocsView space={space} workspaceSlug={workspaceSlug} />
        )}
      </div>
    </div>
  )
}
